import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button-link'
import { Reveal } from '@/components/generals/reveal'
import { SectionLabel } from '@/components/generals/section-label'
import { ProjectCard } from '@/components/landing/project-card'

interface WorkSectionProps {
    projects: ProjectSummary[]
}

export const WorkSection: FC<WorkSectionProps> = ({ projects }): ReactNode => {
    return (
        <section id='work' className='py-20 md:py-28'>
            <div className='page-container'>
                <div className='grid gap-8 border-b border-foreground/15 pb-10 md:grid-cols-12 md:items-end'>
                    <div className='md:col-span-8'>
                        <SectionLabel className='text-muted-foreground'>
                            Selected work
                        </SectionLabel>

                        <h2 className='mt-5 font-heading text-[clamp(3rem,8.5vw,8rem)] font-semibold leading-[0.88] tracking-[-0.045em]'>
                            Recent <em className='font-serif font-normal italic tracking-[-0.02em]'>obsessions</em>

                            {projects.length > 0 && (
                                <sup className='ml-2 align-top font-mono text-sm font-medium tracking-normal text-brand md:text-lg'>
                                    ({String(projects.length).padStart(2, '0')})
                                </sup>
                            )}
                        </h2>
                    </div>

                    <p className='max-w-sm text-lg leading-relaxed text-muted-foreground md:col-span-4 md:justify-self-end'>
                        A few recent projects I'm proud of. Each one started with a brief and ended with a website people actually remember.
                    </p>
                </div>

                {projects.length > 0 ? (
                    <ul className='mt-14 grid gap-x-8 gap-y-16 md:mt-20 md:grid-cols-2 md:gap-x-10 md:gap-y-24 lg:gap-x-16'>
                        {projects.map((project, index) => (
                            <li key={project.id} className={cn(index % 2 === 1 && 'md:mt-40')}>
                                <Reveal>
                                    <ProjectCard project={project} index={index} />
                                </Reveal>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className='mt-14 rounded-2xl border border-dashed border-foreground/25 px-6 py-14 text-center md:py-20'>
                        <p className='font-heading text-2xl font-semibold tracking-[-0.03em] md:text-3xl'>
                            Fresh case studies are on their way.
                        </p>

                        <p className='mx-auto mt-3 max-w-md text-muted-foreground'>
                            In the meantime, I'm happy to walk you through recent work on a quick call.
                        </p>

                        <ButtonLink href='#contact' className='mt-7'>
                            Request a walkthrough
                        </ButtonLink>
                    </div>
                )}
            </div>
        </section>
    )
}
